import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { API_ENDPOINTS } from '@/config/api';
import { authService } from '@/services/authService';
import { userService } from '@/services/userService';
import type { User } from '@/types';

// Types for better type safety
interface AuthResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  user?: {
    name: string;
    email: string;
    roles?: string[];
  };
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const loadingType = ref<'init' | 'logout' | 'login' | 'general'>('init');
  const isInitialized = ref(false);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  // Epoch milliseconds when the access token expires (if provided)
  const tokenExpiry = ref<number | null>(null);
  const error = ref<string | null>(null);

  // Computed
  const isAuthenticated = computed(() => {
    // JWT-only authentication
    const hasValidToken = !!accessToken.value;
    const hasUserData = !!user.value && !!user.value.email;
    
    return hasUserData && hasValidToken;
  });
  
  const isValidJoblogicUser = computed(() => {
    if (!user.value || !user.value.email) return false;
    return user.value.email.endsWith('@joblogic.com');
  });

  // Initialize auth state from localStorage
  const initialize = async () => {
    if (isInitialized.value) {
      console.debug('Auth store already initialized, skipping');
      return;
    }

    console.log('Initializing auth store...');
    
    try {
      isLoading.value = true;
      loadingType.value = 'init';
      
      // Check localStorage for existing session
      const savedUser = localStorage.getItem('joblogic-gpt-user');
      const savedToken = localStorage.getItem('joblogic-gpt-token');
      const savedRefreshToken = localStorage.getItem('joblogic-gpt-refresh-token');
      const savedExpiry = localStorage.getItem('joblogic-gpt-token-expiry');
      
      console.debug('Auth initialization - localStorage check:', {
        hasUser: !!savedUser,
        hasToken: !!savedToken,
        hasRefreshToken: !!savedRefreshToken,
        hasExpiry: !!savedExpiry,
        tokenExpiry: savedExpiry ? new Date(parseInt(savedExpiry, 10)).toISOString() : null
      });
      
      if (savedUser && savedToken) {
        // Restore from localStorage
        user.value = JSON.parse(savedUser);
        accessToken.value = savedToken;
        refreshToken.value = savedRefreshToken;
        tokenExpiry.value = savedExpiry ? parseInt(savedExpiry, 10) : null;
        
        console.log('Restored user session from localStorage:', {
          email: user.value?.email,
          name: user.value?.name,
          tokenPresent: !!accessToken.value,
          tokenExpired: isTokenExpired()
        });
        
        // Load fresh profile data (avatar, preferences) in background
        loadUserProfile().catch(err => {
          console.warn('Failed to refresh user profile during initialization:', err);
        });
        
        // Start periodic session validation (but not immediately)
        startSessionMonitoring();
      } else {
        console.debug('No saved session found in localStorage');
      }
      
      error.value = null;
    } catch (err) {
      console.error('Auth initialization failed:', err);
      error.value = 'Failed to initialize authentication';
      clearAuthData();
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
      console.log('Auth store initialization complete', {
        isAuthenticated: isAuthenticated.value,
        userEmail: user.value?.email
      });
    }
  };

  const loadUserProfile = async () => {
    try {
      if (!user.value) return;
      
      const profileData = await userService.getUserProfile();
      
      // Update user with the complete profile data from the API
      user.value = {
        ...user.value,
        name: profileData.name || user.value.name,
        email: profileData.email || user.value.email,
        avatar_url: profileData.avatar_url,
        preferences: profileData.preferences
      };
      
      // Update localStorage with the enhanced user data
      localStorage.setItem('joblogic-gpt-user', JSON.stringify(user.value));
    } catch (err) {
      console.warn('Failed to load user profile:', err);
      // Non-critical error - don't throw, just log
    }
  };

  const login = () => {
    try {
      // Clear any existing error
      error.value = null;
      
      // Redirect to backend login (which handles Azure AD OAuth)
      window.location.href = API_ENDPOINTS.auth.login;
    } catch (err) {
      console.error('Login initiation failed:', err);
      error.value = 'Failed to initiate login';
    }
  };

  // Handle the auth callback with tokens from Azure
  const handleAuthCallback = async (authData: AuthResponse) => {
    try {
      isLoading.value = true;
      loadingType.value = 'login';
      error.value = null;

      // Store tokens & compute expiry timestamp. Prefer expires_in from response; fallback to JWT exp claim
      accessToken.value = authData.access_token;
      refreshToken.value = authData.refresh_token || null;
      if (authData.expires_in) {
        tokenExpiry.value = Date.now() + authData.expires_in * 1000;
      } else {
        // Try to decode exp from JWT
        try {
          const payload = JSON.parse(atob(authData.access_token.split('.')[1]));
          if (payload.exp) {
            tokenExpiry.value = payload.exp * 1000;
          }
        } catch (e) {
          tokenExpiry.value = null;
        }
      }

      // Get user info from the token or make an API call
      const userInfo = await authService.getUserInfo(authData.access_token);
      
      // Validate that this is a Joblogic user
      if (!userInfo.email.endsWith('@joblogic.com')) {
        throw new Error('Only Joblogic email addresses are allowed');
      }

      // Set user data
      user.value = {
        id: userInfo.email,
        name: userInfo.name || userInfo.email.split('@')[0],
        email: userInfo.email,
        displayName: userInfo.name,
        roles: userInfo.roles || ['user'],
        lastActive: new Date(),
      };

      // Save basic user data to localStorage first
      localStorage.setItem('joblogic-gpt-user', JSON.stringify(user.value));
      localStorage.setItem('joblogic-gpt-token', accessToken.value);
      if (refreshToken.value) {
        localStorage.setItem('joblogic-gpt-refresh-token', refreshToken.value);
      }
      if (tokenExpiry.value) {
        localStorage.setItem('joblogic-gpt-token-expiry', tokenExpiry.value.toString());
      } else {
        localStorage.removeItem('joblogic-gpt-token-expiry');
      }

      // Load additional profile data (avatar, preferences) - non-blocking
      await loadUserProfile();
      
      // Start session monitoring
      startSessionMonitoring();

    } catch (err) {
      console.error('Auth callback failed:', err);
      error.value = err instanceof Error ? err.message : 'Authentication failed';
      clearAuthData();
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    console.log('Logout initiated', {
      reason: 'manual_logout',
      userEmail: user.value?.email,
      hasToken: !!accessToken.value
    });
    
    try {
      isLoading.value = true;
      loadingType.value = 'logout';
      
      // Stop session monitoring
      stopSessionMonitoring();
      
      // Call backend logout endpoint if we have a token
      if (accessToken.value) {
        try {
          console.debug('Calling backend logout endpoint');
          await authService.logout(accessToken.value);
          console.log('Successfully logged out from backend');
        } catch (logoutError) {
          console.error('Backend logout failed:', logoutError);
          // Continue with local cleanup even if backend logout fails
        }
      } else {
        console.debug('No access token, skipping backend logout call');
      }
      
      // Clear all authentication data
      console.debug('Clearing local authentication data');
      clearAuthData();
      
    } catch (err) {
      console.error('Logout failed:', err);
      error.value = 'Logout failed';
      // Still clear local data even if there's an error
      clearAuthData();
    } finally {
      isLoading.value = false;
      console.log('Logout completed');
    }
  };

  const clearAuthData = () => {
    console.debug('Clearing authentication data', {
      hadUser: !!user.value,
      hadToken: !!accessToken.value,
      userEmail: user.value?.email
    });
    
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    tokenExpiry.value = null;
    error.value = null;
    
    // Clear localStorage - JWT-only items
    const itemsToRemove = [
      'joblogic-gpt-user',
      'joblogic-gpt-token',
      'joblogic-gpt-refresh-token',
      'joblogic-gpt-token-expiry',
      'joblogic-gpt-conversations'  // Clear conversations cache
    ];
    
    itemsToRemove.forEach(item => {
      if (localStorage.getItem(item)) {
        console.debug(`Removing localStorage item: ${item}`);
        localStorage.removeItem(item);
      }
    });
    
    console.debug('Authentication data cleared');
  };

  const isTokenExpired = (): boolean => {
    if (!accessToken.value) return true;
    if (!tokenExpiry.value) return false; // If we cannot determine expiry, assume valid until 401
    // Add 5 minute buffer to proactively refresh before actual expiry
    return Date.now() > (tokenExpiry.value - 5 * 60 * 1000);
  };

  const shouldRefreshToken = (): boolean => {
    if (!accessToken.value || !refreshToken.value || !tokenExpiry.value) return false;
    // Refresh if token expires within 10 minutes
    return Date.now() > (tokenExpiry.value - 10 * 60 * 1000);
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken.value) {
      console.debug('No refresh token available for token refresh');
      return false;
    }

    try {
      console.debug('Attempting to refresh access token');
      
      const response = await fetch(API_ENDPOINTS.auth.refresh, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken.value })
      });

      if (!response.ok) {
        console.warn('Token refresh failed:', response.status);
        return false;
      }

      const authData = await response.json();
      
      // Update tokens
      accessToken.value = authData.access_token;
      if (authData.refresh_token) {
        refreshToken.value = authData.refresh_token;
      }
      if (authData.expires_in) {
        tokenExpiry.value = Date.now() + authData.expires_in * 1000;
      }

      // Update localStorage
      localStorage.setItem('joblogic-gpt-token', authData.access_token);
      if (authData.refresh_token) {
        localStorage.setItem('joblogic-gpt-refresh-token', authData.refresh_token);
      }
      if (tokenExpiry.value) {
        localStorage.setItem('joblogic-gpt-token-expiry', tokenExpiry.value.toString());
      }

      console.log('Access token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    // Check if token exists
    if (!accessToken.value) {
      console.debug('No access token available');
      return null;
    }

    // Check if we should refresh the token proactively
    if (shouldRefreshToken()) {
      console.debug('Token expiring soon, attempting refresh');
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        console.warn('Token refresh failed, token may be expired');
        // Don't immediately logout, let the server validate
      }
    }
    
    return accessToken.value;
  };

  let sessionMonitoringInterval: NodeJS.Timeout | null = null;
  let sessionCheckFailures = 0;
  const MAX_SESSION_CHECK_FAILURES = 5; // Increased from 3 to be more tolerant

  const startSessionMonitoring = () => {
    // Check session every 30 minutes (increased from 15 minutes)
    if (sessionMonitoringInterval) {
      clearInterval(sessionMonitoringInterval);
    }
    
    // Reset failure count when starting monitoring
    sessionCheckFailures = 0;
    
    // Start monitoring after a longer delay to avoid immediate checks on page load
    setTimeout(() => {
      sessionMonitoringInterval = setInterval(async () => {
        if (!isAuthenticated.value) {
          console.debug('Not authenticated, stopping session monitoring');
          stopSessionMonitoring();
          return;
        }
        
        try {
          console.debug('Performing periodic session check');
          
          // Try to refresh token if it's expiring soon
          if (shouldRefreshToken()) {
            console.debug('Token expiring soon, refreshing during session check');
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              console.debug('Token refreshed successfully during session check');
              sessionCheckFailures = 0; // Reset on successful refresh
              return;
            }
          }
          
          const sessionCheck = await authService.checkSession();
          
          if (!sessionCheck.valid) {
            sessionCheckFailures++;
            console.warn(`Session check failed (${sessionCheckFailures}/${MAX_SESSION_CHECK_FAILURES})`, {
              expired: sessionCheck.expired,
              consecutiveFailures: sessionCheckFailures
            });
            
            // Only logout after multiple consecutive failures or explicit expiry
            if (sessionCheck.expired || sessionCheckFailures >= MAX_SESSION_CHECK_FAILURES) {
              console.log('Session definitively expired or too many failures - logging out');
              await logout();
              const redirectPath = window.location.pathname + window.location.search;
              window.location.href = `/login?error=session_expired&redirect=${encodeURIComponent(redirectPath)}`;
            }
          } else {
            // Reset failure count on successful check
            if (sessionCheckFailures > 0) {
              console.debug('Session check successful, resetting failure count');
              sessionCheckFailures = 0;
            }
          }
        } catch (error) {
          sessionCheckFailures++;
          console.warn(`Session monitoring check failed (${sessionCheckFailures}/${MAX_SESSION_CHECK_FAILURES}):`, error);
          
          // Only logout if we've had too many consecutive failures
          // Network errors shouldn't immediately log users out
          if (sessionCheckFailures >= MAX_SESSION_CHECK_FAILURES) {
            console.error('Too many consecutive session check failures, logging out');
            await logout();
            const redirectPath = window.location.pathname + window.location.search;
            window.location.href = `/login?error=connection_issues&redirect=${encodeURIComponent(redirectPath)}`;
          }
        }
      }, 30 * 60 * 1000); // 30 minutes (increased from 15)
    }, 10 * 60 * 1000); // Start after 10 minutes (increased from 5)
  };

  const stopSessionMonitoring = () => {
    if (sessionMonitoringInterval) {
      clearInterval(sessionMonitoringInterval);
      sessionMonitoringInterval = null;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  // Legacy function kept for backward compatibility
  const handleCallback = async (code: string, state: string) => {
    console.warn('handleCallback is deprecated - use handleAuthCallback instead');
    return Promise.resolve();
  };

  // Return store interface
  return {
    // State
    user,
    isLoading,
    loadingType,
    isInitialized,
    accessToken,
    refreshToken,
    error,
    
    // Computed
    isAuthenticated,
    isValidJoblogicUser,
    
    // Actions
    initialize,
    login,
    loadUserProfile,
    handleCallback, // Deprecated but kept for compatibility
    handleAuthCallback, // New method for token-based auth
    logout,
    getAccessToken,
    isTokenExpired,
    shouldRefreshToken,
    refreshAccessToken,
    clearError,
  };
});
